import { useEffect, useId, useRef, useState } from 'react';

import { formatHours, type ProjectArchiveImpact, type ProjectDto } from '@time-tracking/shared';

import { useFocusTrap } from '../../../hooks/useFocusTrap.js';

interface ArchiveProjectDialogProps {
  /** The project to act on, or `null` when the dialog is closed. */
  project: ProjectDto | null;
  /** Archive impact for `project`, once loaded. */
  impact: ProjectArchiveImpact | undefined;
  impactLoading: boolean;
  impactError: boolean;
  onRetryImpact: () => void;
  onClose: () => void;
  /**
   * `observedLoggedMinutes` is the impact figure this dialog decided
   * `releaseCode` from. The server re-checks it under the archive's own locks,
   * so an hour landing while the dialog sat open cannot quietly turn a free
   * code release into one that rewrites historical report rows.
   */
  onConfirm: (opts: { releaseCode: boolean; observedLoggedMinutes: number }) => void;
  isPending: boolean;
}

function plural(count: number, singular: string): string {
  return count === 1 ? singular : `${singular}s`;
}

/**
 * One dialog, both directions — archive and restore.
 *
 * They share a component because they share every structural concern: the same
 * focus trap, the same Escape-suppressed-while-pending contract, the same
 * impact fetch, and the same "confirm is disabled until we know what we are
 * telling you" gate. Restore is gated too, on purpose: it is a one-click fan-out
 * that puts rows back on many people's timesheets, so gating archive but not
 * restore would be backwards.
 *
 * DESTRUCTIVE INTENT IS CARRIED BY COPY, NOT COLOUR. Red is off-brand per Brand
 * Book V2.1 (see the note in ConfirmDialog.tsx), so the confirm button keeps the
 * standard treatment and the consequences are spelled out in the body. The
 * code-release caution uses Tangerine, the sanctioned caution accent, and always
 * pairs it with an icon and the word "Careful" so colour is never the only cue.
 */
export function ArchiveProjectDialog({
  project,
  impact,
  impactLoading,
  impactError,
  onRetryImpact,
  onClose,
  onConfirm,
  isPending,
}: ArchiveProjectDialogProps) {
  const dialogRef = useRef<HTMLDivElement>(null);
  const confirmBtnRef = useRef<HTMLButtonElement>(null);
  const titleId = useId();
  const descId = useId();
  const teamsLabelId = useId();
  const codeHelpId = useId();

  const open = project !== null;
  const isRestore = Boolean(project?.archivedAt);
  // Confirm is the intended landing spot, but it is disabled until the impact
  // lands — the trap falls back to Cancel in that window rather than leaving
  // focus outside the dialog. One authoritative move, not two racing ones.
  useFocusTrap(dialogRef, open, confirmBtnRef);

  // `null` means "the manager has not touched the checkbox", so the effective
  // value falls back to the impact-derived default. Keeping the untouched state
  // distinct is what lets the default arrive late (with the impact) without
  // overwriting a choice already made.
  const [releaseCodeChoice, setReleaseCodeChoice] = useState<boolean | null>(null);

  // Reset on the project identity, not the project object: the object is query
  // cache data and gets a new reference on every refetch, which would wipe the
  // manager's checkbox mid-dialog.
  const projectId = project?.projectId ?? null;
  useEffect(() => {
    setReleaseCodeChoice(null);
  }, [projectId]);

  useEffect(() => {
    if (!open) return;
    function handleKeyDown(e: KeyboardEvent) {
      // No dismissal while the mutation is in flight — the manager would be left
      // unsure whether it landed.
      if (e.key === 'Escape' && !isPending) onClose();
    }
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [open, isPending, onClose]);

  const pendingLabel = isRestore ? 'Restoring…' : 'Archiving…';

  // Everything the live region can say, as one string. Loading, in-flight, and
  // the load failure share it deliberately: one region cannot announce over
  // itself, and routing the failure through here is what makes a repeated
  // failure audible — the text passes back through "Checking…" on every retry,
  // so two identical errors are still two changes.
  const statusText = impactLoading
    ? 'Checking what this affects…'
    : isPending
      ? pendingLabel
      : impactError
        ? 'Could not load what this affects. Nothing has changed.'
        : '';

  // Held in state and assigned by an effect, never rendered inline. The region
  // has to exist in the accessibility tree for a commit BEFORE it has text —
  // one inserted already-populated is not announced, and the dialog's own mount
  // is that insertion. Clearing it while closed is what keeps that true for the
  // second and every later open.
  const [liveMessage, setLiveMessage] = useState('');
  useEffect(() => {
    setLiveMessage(open ? statusText : '');
  }, [open, statusText]);

  if (!project) return null;

  const impactReady = !impactLoading && !impactError && impact !== undefined;
  // Loading and errored are both "we do not know yet", and both keep confirm
  // disabled — but they are never conflated: an error gets its own alert and a
  // retry, rather than falling through to confident zero-impact copy.
  const confirmDisabled = isPending || !impactReady;

  const hasHours = (impact?.loggedMinutes ?? 0) > 0;
  const releaseCode = releaseCodeChoice ?? !hasHours;

  const title = isRestore ? 'Restore project' : 'Archive project';
  const confirmLabel = isRestore ? 'Restore project' : 'Archive project';

  return (
    <div
      className="dialog-backdrop"
      onClick={(e) => {
        if (e.target === e.currentTarget && !isPending) onClose();
      }}
    >
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        aria-describedby={descId}
        className="projects-dialog"
        style={{ maxWidth: 520 }}
      >
        <h2 id={titleId} className="dialog-title">
          {title}
        </h2>

        <div className="dialog-body" aria-busy={impactLoading}>
          <p id={descId} className="archive-dialog__lede">
            {isRestore ? 'Restore ' : 'Archive '}
            <strong>{project.name}</strong> (<code>{project.code}</code>)?
          </p>

          <p className="archive-dialog__prose">
            {isRestore
              ? 'The staffing recorded when it was archived goes back into effect, so those rows start appearing on people’s timesheets again. Anyone removed from a team since — and any task archived since — stays off.'
              : 'It stops seeding rows onto anyone’s timesheet and moves to the Archived group in the sidebar. Every hour already logged is kept and still counts in reports. You can restore it later.'}
          </p>

          {/* The single live region for everything this dialog has to say:
              loading, in-flight, and the load failure below.

              It renders EMPTY on the first commit and is filled by an effect, so
              the region is in the accessibility tree before any text arrives. A
              region inserted already-populated is not announced — the dialog's
              own mount is that insertion, so `role="status"` with the message
              inlined would have been silent on open. Same rule, and the same
              reason, as `ToastRegion`.

              Routing the load failure through here as well is what makes a
              REPEATED failure audible: the text passes through "Checking what
              this affects…" on each retry, so identical failures are still a
              change, and there is no second live region racing this one. The
              error box below is left visual-only and keeps its "Try again"
              button — re-announcing by remounting it would throw away focus the
              user just placed there. */}
          <p className="archive-dialog__status" role="status">
            {/* Hidden visually — never from the region — while the error box is
                up, because that box already shows this sentence to sighted
                users. Rendering it in both places put the same line on screen
                twice. The announcement is unaffected: the region's text still
                changes, which is the only thing a live region reacts to. */}
            {impactError ? <span className="sr-only">{liveMessage}</span> : liveMessage}
          </p>

          {/* Not role="alert": the status region above announces this, and two
              live regions would double-announce. See the comment there. */}
          {impactError && (
            <div className="archive-dialog__error">
              <svg
                width="16"
                height="16"
                viewBox="0 0 16 16"
                fill="currentColor"
                aria-hidden="true"
                className="archive-dialog__error-icon"
              >
                <path d="M8 1.5l6.5 12h-13L8 1.5zm0 4a.75.75 0 0 0-.75.75v3.5a.75.75 0 0 0 1.5 0v-3.5A.75.75 0 0 0 8 5.5zm0 6.25a.9.9 0 1 0 0 1.8.9.9 0 0 0 0-1.8z" />
              </svg>
              <p className="archive-dialog__error-text">
                Could not load what this affects. Nothing has changed.
              </p>
              <button type="button" className="btn btn--secondary btn--sm" onClick={onRetryImpact}>
                Try again
              </button>
            </div>
          )}

          {impactReady && (
            <>
              <ul className="archive-impact__list">
                <li>
                  <strong>{impact.taskCount}</strong> {plural(impact.taskCount, 'task')}
                </li>
                {!isRestore && (
                  <li>
                    <strong>{impact.assignedUserCount}</strong>{' '}
                    {impact.assignedUserCount === 1 ? 'person' : 'people'} currently assigned
                  </li>
                )}
                <li>
                  <strong>{formatHours(impact.loggedMinutes)}h</strong> logged — kept, and still in
                  reports
                </li>
              </ul>

              {impact.affectedTeams.length > 0 && (
                <div className="archive-impact__teams">
                  <p id={teamsLabelId} className="archive-impact__teams-label">
                    {isRestore ? 'Teams whose staffing comes back' : 'Teams this cuts staffing for'}
                  </p>
                  <ul aria-labelledby={teamsLabelId} className="archive-impact__teams-list">
                    {impact.affectedTeams.map((team) => (
                      <li key={team.teamId}>
                        {team.name} · {team.affectedMemberCount}{' '}
                        {plural(team.affectedMemberCount, 'member')}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {!isRestore && impact.pendingApprovalCount > 0 && (
                <p className="archive-dialog__note">
                  {impact.pendingApprovalCount} {plural(impact.pendingApprovalCount, 'timesheet')}{' '}
                  {impact.pendingApprovalCount === 1 ? 'is' : 'are'} awaiting approval. Archiving
                  does not affect them; you can still approve them.
                </p>
              )}

              {!isRestore && (
                <>
                  <div className="dialog-form-field dialog-form-field--checkbox">
                    <label>
                      <input
                        type="checkbox"
                        checked={releaseCode}
                        onChange={(e) => setReleaseCodeChoice(e.target.checked)}
                        // The value was captured when confirm fired, so toggling
                        // it mid-flight changes nothing — leaving it live would
                        // say otherwise. Matches Cancel and Confirm.
                        disabled={isPending}
                        aria-describedby={codeHelpId}
                      />
                      <span>
                        Also free the project code <code>{project.code}</code> for reuse
                      </span>
                    </label>
                  </div>

                  {/* The checkbox's description, and it follows the checkbox.
                      The caution is about what freeing the code DOES, so showing
                      it while the box is unticked warns about an action the
                      manager has not chosen — and, since the box defaults off
                      exactly when hours exist, that was its default state.

                      One persistent element, not a branch that swaps elements:
                      it is a live region, and ticking the box is a user action
                      whose consequence has to reach a screen reader without
                      focus moving (WCAG 4.1.3). A screen reader reads a
                      checkbox's description when focus ARRIVES, not when the
                      state changes, so without this you tick the box and hear
                      "checked" and nothing else. A region swapped in
                      already-populated is not announced — the same rule the
                      status region above and `ToastRegion` are built around.
                      It also has to keep resolving `aria-describedby` in every
                      state, which a bare `{cond && …}` would not.

                      Unlike the status region, this one does NOT commit empty
                      and fill via an effect, so its first content — on the tick
                      where the impact lands and the checkbox appears — is not
                      announced. Deliberate: that content is only ever a neutral
                      hint. The caution cannot be there at mount, because
                      reaching it requires the tick this region exists to
                      announce.

                      Two live regions therefore co-exist in this dialog. They
                      never both SPEAK at once: this one moves only on a tick,
                      and the box is disabled while the mutation is in flight.
                      (They do both change on the tick where the impact lands —
                      the status line emptying, this one mounting — and neither
                      utters anything then.) */}
                  <div id={codeHelpId} role="status">
                    {!releaseCode ? (
                      <p className="archive-dialog__hint">
                        <code>{project.code}</code> stays with this project, so it is not available
                        for reuse.
                      </p>
                    ) : hasHours ? (
                      <div className="archive-dialog__caution">
                        <svg
                          width="16"
                          height="16"
                          viewBox="0 0 16 16"
                          fill="currentColor"
                          aria-hidden="true"
                          className="archive-dialog__caution-icon"
                        >
                          <path d="M8 1.5l6.5 12h-13L8 1.5zm0 4a.75.75 0 0 0-.75.75v3.5a.75.75 0 0 0 1.5 0v-3.5A.75.75 0 0 0 8 5.5zm0 6.25a.9.9 0 1 0 0 1.8.9.9 0 0 0 0-1.8z" />
                        </svg>
                        <p className="archive-dialog__caution-text">
                          <strong>Careful:</strong> {formatHours(impact.loggedMinutes)}h have
                          already been logged against <code>{project.code}</code>. Reports read the
                          project&rsquo;s current code, so freeing it changes the code shown on
                          every past report row for this project — and restoring later does not take{' '}
                          <code>{project.code}</code> back.
                        </p>
                      </div>
                    ) : (
                      <p className="archive-dialog__hint">
                        Nothing has ever been logged against this project, so no report reads its
                        code. Archiving renames it with an <code>-archived-</code> suffix and{' '}
                        <code>{project.code}</code> becomes available again.
                      </p>
                    )}
                  </div>
                </>
              )}
            </>
          )}
        </div>

        <div className="dialog-footer">
          <button
            type="button"
            className="btn btn--secondary"
            onClick={onClose}
            disabled={isPending}
          >
            Cancel
          </button>
          <button
            ref={confirmBtnRef}
            type="button"
            className="btn btn--primary"
            onClick={() =>
              onConfirm({ releaseCode, observedLoggedMinutes: impact?.loggedMinutes ?? 0 })
            }
            disabled={confirmDisabled}
            aria-busy={isPending}
          >
            {isPending ? pendingLabel : confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}
